import _ from 'lodash'
import { JSONEditor } from '@json-editor/json-editor'
import Choices from 'choices.js'
import Mustache from 'mustache'

const ArrayEditor = JSONEditor.defaults.editors.array

class LargeArrayEditor extends JSONEditor.AbstractEditor {
  constructor (options, defaults) {
    super(options, defaults)

    console.log('LargeArrayEditor::constructor')
  
    this.refreshValue = _.debounce((force) => {
      // console.log('refreshValue')
      this.updateChoices()
      this.serialized = JSON.stringify(this.value)
    }, 1)
  
    this.onChildEditorChange = _.debounce((editor) => {
      if (this.ready && this.currentIndex >= 0) {
        try {
          // console.log(editor.getValue())
          if (this.currentIndex >= 0) {
            this.value[this.currentIndex] = editor.getValue()
            this.refreshValue()
            super.onChildEditorChange(editor)
          }
        } catch (err) {
          console.error(err)
        }
      }
    }, 1)
  }

  register () {
    super.register()
    this.child.register()
  }
  
  unregister () {
    super.unregister()
    this.child.unregister()
  }
  
  preBuild () {
    // console.log('preBuild')
    this.ready = false
    this.child = null
    this.currentIndex = undefined
  }
  
  build () {
    this.container.classList.add('large-array')

    this.header = document.createElement('span')
    this.header.textContent = this.getTitle()
    this.title = this.theme.getHeader(this.header)
    this.container.appendChild(this.title)
    this.titleControls = this.theme.getHeaderButtonHolder()
    this.title.appendChild(this.titleControls)

    if (this.schema.description) {
      this.description = this.theme.getDescription(this.schema.description)
      this.container.appendChild(this.description)
    }

    this.controls = this.theme.getHeaderButtonHolder()
    this.title.appendChild(this.controls)

    this.input = this.theme.getSelectInput()
    this.input.addEventListener('choice', ({ detail }) => {
      this.selectChild(detail.choice.value)
    })

    this.holder = this.theme.getChildEditorHolder()
    this.holder.classList.add('large-array-child')
    this.holder.classList.add('card')
    this.holder.classList.add('card-body')
    this.holder.classList.add('bg-white')
    this.holder.style.display = 'none'

    this.controls.classList.add('large-array-controls')

    this.deleteButton = this.getButton('', 'delete', this.translate('button_delete_row_title', ['Item']))
    this.deleteButton.classList.add('delete')
    this.deleteButton.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.deleteChild()
    })

    const buttonLabel = _.get(this.options, 'buttonLabel', this.getItemTitle())

    this.addButton = this.getButton(buttonLabel, 'add', this.translate('button_add_row_title', ['Item']))
    this.addButton.classList.add('add')
    this.addButton.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.addChild()
    })

    this.copyButton = this.getButton('', 'copy', this.translate('button_copy_row_title', ['Item']))
    this.copyButton.classList.remove('btn-outline-secondary')
    this.copyButton.classList.add('btn-secondary')
    this.copyButton.classList.add('copy')
    this.copyButton.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.duplicateChild()
    })

    this.titleControls.appendChild(this.input)
    this.titleControls.appendChild(this.addButton)

    this.controls.appendChild(this.copyButton)
    this.controls.appendChild(this.deleteButton)

    var editor = this.jsoneditor.getEditorClass(this.schema.items)

    this.child = this.jsoneditor.createEditor(editor, {
      jsoneditor: this.jsoneditor,
      schema: this.schema.items,
      container: this.holder,
      path: this.path + '.child',
      parent: this,
      required: true,
      disable_properties: true
    })

    this.container.appendChild(this.holder)

    this.child.preBuild()
    this.child.build()
    this.child.postBuild()

    if (this.child.title) {
      this.child.title.insertBefore(this.controls, this.child.title.childNodes[this.child.title.childNodes.length - 1])
    }
    const template = this.schema.items.headerTemplate
    this.itemTitleTmpl = (data) => Mustache.render(template, data)

    this.choices = new Choices(this.input, {
      itemSelectText: '',
      searchFields: ['label'],
      searchResultLimit: 100
    })
  }
  
  postBuild () {
    super.postBuild()
    // console.log('ready')
    this.ready = true
  }

  getDefault () {
    return this.schema.default || []
  }

  getMax () {
    return ArrayEditor.prototype.getMax.apply(this, arguments)
  }
  
  setValue (value = [], initial) {
    // console.log('setValue', value)
    value = ArrayEditor.prototype.ensureArraySize.call(this, value)

    const serialized = JSON.stringify(value)
    if (serialized === this.serialized) return

    // console.log('value is new')
    this.value = value

    if (this.value && this.value.length) {
      _.defer(() => {
        this.selectChild(_.max([this.currentIndex, 0]))
      })
    }

    this.refreshValue(initial)
    this.onChange(true)
  }

  updateChoices () {
    if (this.value && this.value.length) {
      const choices = _.map(this.value, (option, index) => {
        return {
          value: index,
          label: this.getItemLabel(option, index),
          selected: index === this.currentIndex,
          disabled: false
        }
      })

      this.copyButton.style.display = ''
      this.deleteButton.style.display = ''
      this.holder.style.display = ''

      this.choices.setChoices(choices, 'value', 'label', true)
      this.choices.enable()
    } else {
      this.choices.setChoices([{
        value: '',
        label: 'No items available',
        selected: true,
        disabled: true
      }], 'value', 'label', true)
      this.choices.disable()

      this.copyButton.style.display = 'none'
      this.deleteButton.style.display = 'none'
      this.holder.style.display = 'none'
    }
  }

  selectChild (index) {
    // console.log('selectChild', index)
    if (index !== undefined) {
      this.currentIndex = index
    }
    this.updateChildValue(this.currentIndex)
  }

  updateChildValue (index, value = undefined) {
    // console.log('updateChildValue', index)
    if (value === undefined) {
      value = this.value[index] || { }
    }

    if (value) {
      this.child.path = `${this.path}.child.${index}`
      this.child.key = index
      this.child.setValue(value, false)
      this.child.header.innerHTML = `<span class="badge badge-info">${this.getItemLabel(value, index)}</span>`
      this.holder.style.display = ''
    } else {
      this.child.setValue({ }, false)
      this.holder.style.display = 'none'
    }
  }

  deleteChild (index) {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return
    }

    index = _.isNumber(index) ? index : this.currentIndex

    const newval = this.getValue().filter((row, j) => j !== index)
    this.currentIndex = _.max([index - 1, newval.length - 1])

    const editor = this.child

    this.setValue(newval)
    
    this.onChange(true)
    this.jsoneditor.trigger('deleteRow', editor)
  }
  
  addChild (child) {
    child = child || _.extend({ }, this.schema.items.default)

    // insert empty value
    this.value.push(child)
    this.currentIndex = this.value.length - 1
    this.updateChildValue(this.currentIndex)

    const editor = this.child

    this.setValue(this.value)

    this.onChange(true)
    this.jsoneditor.trigger('addRow', editor)
  }
  
  duplicateChild (index) {
    index = _.isNumber(index) ? index : this.currentIndex
    const copy = _.cloneDeep(this.value[index])
    this.addChild(copy)
  }
  
  getItemId (option, index) {
    const title = this.schema.items.title || 'item'
    const compiled = this.itemTitleTmpl({ self: option, i: index, i1: index + 1 })
    return compiled || _.kebabCase(title + ' ' + (index + 1))
  }

  getItemLabel (option, index) {
    const title = this.schema.items.title || 'item'
    const compiled = this.itemTitleTmpl({ self: option, i: index, i1: index + 1 })
    return compiled || _.startCase(title + ' ' + (index + 1))
  }
  
  getItemTitle () {
    return ArrayEditor.prototype.getItemTitle.apply(this, arguments)
  }
}

export default LargeArrayEditor
